import { useState } from "react";
import { Send, Loader2, CheckCircle2, AlertCircle, Instagram, Linkedin, Youtube, Facebook, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/data/content";
import { submitForm } from "@/lib/api";
import logoIcon from "@/assets/logo-icon.png";

const COMPANY = [
  { label: "About", href: "#about" },
  { label: "Why Choose Us", href: "#why" },
  { label: "Success Stories", href: "#stories" },
  { label: "Placements", href: "#partners" },
  { label: "Blog", href: "#blog" },
  { label: "Contact", href: "#contact" },
];

const ABROAD = [
  { label: "Study in USA", href: "#abroad" },
  { label: "Study in Canada", href: "#abroad" },
  { label: "Study in UK", href: "#abroad" },
  { label: "Study in Australia", href: "#abroad" },
  { label: "Study in Germany", href: "#abroad" },
  { label: "Scholarships", href: "#abroad" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: "success" | "error", message: string }

  const subscribe = async (e) => {
    e.preventDefault();
    if (loading) return;

    const trimmed = email.trim();

    if (!trimmed) {
      setStatus({ type: "error", message: "Please enter your email address." });
      toast.error("Please enter your email address.");
      return;
    }

    if (!EMAIL_RE.test(trimmed)) {
      setStatus({ type: "error", message: "Please enter a valid email address." });
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      await submitForm("/newsletter", { email: trimmed });
      setStatus({ type: "success", message: "You're on the list. Watch your inbox!" });
      toast.success("You're on the list. Watch your inbox!");
      setEmail("");
    } catch (err) {
      const s = err?.response?.status;
      if (s === 409) {
        setStatus({ type: "success", message: "You're already subscribed." });
        toast.info("You're already subscribed.");
        setEmail("");
      } else if (s === 422 || s === 400) {
        setStatus({ type: "error", message: "Please enter a valid email address." });
        toast.error("Please enter a valid email address.");
      } else {
        setStatus({
          type: "error",
          message: "Could not subscribe right now. Please try again in a moment.",
        });
        toast.error("Could not subscribe. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (href) => {
    if (!href.startsWith("#")) return;
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#0A3161] text-white">
      {/* Decorative top wave */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#F8F9FA] to-transparent pointer-events-none opacity-0" aria-hidden />

      <div className="container-x pt-16 md:pt-20 pb-10 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5">
              <span className="h-12 w-12 shrink-0 rounded-2xl bg-white/95 p-1.5 flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
                <img src={logoIcon} alt="IIUTCStudy logo" className="h-full w-full object-contain" />
              </span>
              <div className="leading-tight">
                <div className="font-display font-extrabold text-xl">
                  <span style={{ color: "#940835" }}>IIUTC</span>
                  <span className="text-white">Study</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/60">
                  {BRAND.tagline}
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/75 leading-relaxed max-w-sm">
              {BRAND.fullName}. Career-first technology & commerce training, with world-class study abroad consulting under one roof.
            </p>

            <form
              onSubmit={subscribe}
              noValidate
              className="mt-6 max-w-sm"
              data-testid="newsletter-form"
            >
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status) setStatus(null);
                  }}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  aria-label="Email address"
                  aria-invalid={status?.type === "error"}
                  placeholder="your.email@work.com"
                  data-testid="newsletter-input"
                  className={`flex-1 min-w-[200px] h-11 rounded-full border bg-white/10 text-white placeholder-white/50 px-4 text-sm focus:outline-none focus:ring-2 transition-colors ${
                    status?.type === "error"
                      ? "border-[#F5B7B4] focus:border-[#F5B7B4] focus:ring-[#F5B7B4]/20"
                      : "border-white/15 focus:border-white/30 focus:ring-white/10"
                  }`}
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#B31942] text-white px-4 h-11 text-sm font-semibold hover:bg-[#940835] disabled:opacity-70 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2.25} />}
                  Subscribe
                </button>
              </div>
              {status && (
                <p
                  data-testid="newsletter-status"
                  role="status"
                  className={`mt-2.5 flex items-center gap-1.5 text-xs font-medium ${
                    status.type === "success" ? "text-emerald-300" : "text-[#F5B7B4]"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
                  ) : (
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
                  )}
                  {status.message}
                </p>
              )}
            </form>

            <div className="mt-6 flex items-center gap-2">
              {[
                { Icon: Instagram, href: BRAND.social.instagram, k: "instagram" },
                { Icon: Linkedin, href: BRAND.social.linkedin, k: "linkedin" },
                { Icon: Youtube, href: BRAND.social.youtube, k: "youtube" },
                { Icon: Facebook, href: BRAND.social.facebook, k: "facebook" },
              ].map(({ Icon, href, k }) => (
                <a
                  key={k}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${k}`}
                  className="h-10 w-10 rounded-2xl border border-white/15 flex items-center justify-center text-white/85 hover:text-white hover:bg-white/10 hover:border-white/30 transition-colors"
                >
                  <Icon className="h-4 w-4" strokeWidth={2.25} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Company</div>
              <ul className="mt-4 space-y-2.5">
                {COMPANY.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => scrollTo(l.href)}
                      data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-white/80 hover:text-white transition-colors"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Study Abroad</div>
              <ul className="mt-4 space-y-2.5">
                {ABROAD.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => scrollTo(l.href)}
                      data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-white/80 hover:text-white transition-colors text-left"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Contact</div>
            <ul className="mt-4 space-y-3.5">
              <li className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-4 w-4" strokeWidth={2.25} />
                </span>
                <span className="text-sm text-white/85 leading-relaxed">{BRAND.address}</span>
              </li>
              <li>
                <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-white/85 hover:text-white transition-colors">
                  <span className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <span className="text-sm font-semibold">{BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-white/85 hover:text-white transition-colors">
                  <span className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <span className="text-sm font-semibold break-all">{BRAND.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} IIUTCStudy — {BRAND.fullName}. All rights reserved.
            <br className="sm:hidden" />
            <span className="sm:ml-1">
              Developed by{" "}
              <a
                href="https://qwikdigitalsolutions.com/?utm_source=chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-white underline underline-offset-2 transition-colors"
              >
                Qwik Digital AND IT SOLUTIONS
              </a>
            </span>
          </p>
          <div className="flex items-center gap-5 text-xs text-white/60">
            <button onClick={() => scrollTo("#faq")} className="hover:text-white transition-colors">FAQ</button>
            <button
              onClick={() => toast.info("Our Privacy Policy is available on request — contact us on WhatsApp or email.")}
              data-testid="footer-privacy"
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => toast.info("Our Terms & Conditions are available on request — contact us on WhatsApp or email.")}
              data-testid="footer-terms"
              className="hover:text-white transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
